import React, { useEffect, useRef } from "react";

const LOGO_FIREWORK_COLORS = ["#4f46e5", "#7c3aed", "#22d3ee", "#38bdf8", "#a855f7", "#0ea5e9"];

// P0 Reverted: 20 fireworks, faster speed for more exciting animation
const createShellConfigs = (width, height, count = 20) =>
  Array.from({ length: count }, (_, idx) => {
    const sizeScale = 0.6 + Math.random() * 0.7;
    return {
      id: idx + 1,
      x: width * (0.1 + Math.random() * 0.8),
      delay: idx * 120 + Math.random() * 80, // Faster launch sequence
      targetY: height * (0.15 + Math.random() * 0.25),
      sizeScale,
      color: LOGO_FIREWORK_COLORS[idx % LOGO_FIREWORK_COLORS.length],
    };
  });

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const bigint = parseInt(value.length === 3 ? value.split("").map((c) => c + c).join("") : value, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function FireworksCanvas({ active, onComplete }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2); // Cap at 2x for performance
    
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const scale = Math.max(0.7, Math.min(1.2, height / 800));
    const gravity = 0.04 * scale;
    const shellSpeed = height * 0.013; // Slightly faster shells

    const shells = [];
    const particles = [];
    const shellConfigs = createShellConfigs(width, height, 20); // P0 Reverted: 20 fireworks
    const start = performance.now();
    const maxDuration = 6000; // P0 Reverted: Longer duration for more show

    const createShell = (config) => ({
      x: config.x,
      y: height + 20,
      vx: (Math.random() - 0.5) * 0.8,
      vy: -shellSpeed * (1 + Math.random() * 0.1) * config.sizeScale,
      targetY: config.targetY,
      color: config.color,
      sizeScale: config.sizeScale,
      trail: [],
      exploded: false,
    });

    const addParticle = (particle) => {
      particle.trail = [];
      particles.push(particle);
    };

    // P0 Reverted: Full explosion with more particles for exciting animation
    const explode = (shell) => {
      const burstCount = Math.round(50 * shell.sizeScale + 30);
      const baseSpeed = 4.0 * scale * shell.sizeScale;
      for (let i = 0; i < burstCount; i += 1) {
        const angle = (Math.PI * 2 * i) / burstCount;
        const speed = baseSpeed * (0.75 + Math.random() * 0.5);
        addParticle({
          x: shell.x,
          y: shell.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 120 + Math.random() * 60,
          decay: 1.0,
          drag: 0.985,
          gravity: 0.45,
          color: shell.color,
          size: 2.2 * shell.sizeScale,
          trailLength: 8,
        });
      }

      // P0 Reverted: Full willow effect
      const willowCount = Math.round(60 * shell.sizeScale + 40);
      for (let i = 0; i < willowCount; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = baseSpeed * (0.3 + Math.random() * 0.25);
        addParticle({
          x: shell.x,
          y: shell.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 180 + Math.random() * 100,
          decay: 0.8,
          drag: 0.99,
          gravity: 0.85,
          color: shell.color,
          size: 1.6 * shell.sizeScale,
          trailLength: 14,
        });
      }
    };

    // P0 Reverted: Full trail drawing for better visuals
    const drawTrail = (trail, color, widthPx) => {
      if (trail.length < 2) return;
      ctx.lineWidth = widthPx;
      ctx.lineCap = "round";
      for (let i = 0; i < trail.length - 1; i += 1) {
        const p1 = trail[i];
        const p2 = trail[i + 1];
        const alpha = i / trail.length;
        ctx.strokeStyle = toRgba(color, alpha * 0.85);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
    };

    const animate = (now) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter";

      shellConfigs.forEach((config) => {
        if (config.launched || elapsed < config.delay) return;
        config.launched = true;
        shells.push(createShell(config));
      });

      shells.forEach((shell) => {
        if (shell.exploded) return;
        shell.vy += gravity;
        shell.x += shell.vx;
        shell.y += shell.vy;
        shell.trail.push({ x: shell.x, y: shell.y });
        if (shell.trail.length > 8) shell.trail.shift();
        drawTrail(shell.trail, shell.color, 2.0);
        ctx.fillStyle = shell.color;
        ctx.beginPath();
        ctx.arc(shell.x, shell.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        if (shell.y <= shell.targetY || shell.vy >= 0) {
          shell.exploded = true;
          explode(shell);
        }
      });

      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.vx *= p.drag;
        p.vy = p.vy * p.drag + gravity * p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > p.trailLength) p.trail.shift();
        drawTrail(p.trail, p.color, p.size);
        const alpha = Math.max(p.life / 180, 0);
        ctx.fillStyle = toRgba(p.color, alpha);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        if (p.life <= 0) particles.splice(i, 1);
      }

      const allShellsExploded = shells.length && shells.every((s) => s.exploded);
      if (!doneRef.current && elapsed > maxDuration && allShellsExploded && particles.length === 0) {
        doneRef.current = true;
        onComplete?.();
        return;
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    const fallbackTimer = setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      onComplete?.();
    }, maxDuration + 600);

    return () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(fallbackTimer);
      window.removeEventListener("resize", resize);
    };
  }, [active, onComplete]);

  if (!active) return null;

  return (
    <div className="fireworks-overlay" data-testid="appointment-fireworks-overlay">
      <canvas ref={canvasRef} className="h-full w-full" data-testid="appointment-fireworks-canvas" />
    </div>
  );
}